import moment, { Moment } from 'moment-timezone'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButtonCircle, { PerButtonCircleProps } from '../../../components/custom_components/ButtonCircle'
import PerDatePicker from '../../../components/custom_components/DatePicker'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerModal } from '../../../components/custom_components/Modal'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { setNextAppraisalSeasonOpeningDate } from '../../../graphql/appraisal-season/appraisalSeasonMutations'
import { useDateTime } from '../../../shared/useDateTime'
import { AppraisalSeasonOpeningInfoDto } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'
import { useHRSettingsStore } from './HRSettingsStore'
import { showNotification } from '../../../components/custom_components/Notification/notification'

interface InfoLabelAndTextProps {
  label?: string
  text?: string
  labelClassName?: string
  textClassName?: string
  rowClassName?: string
}

interface InfoLabelAndTextAndButtonProps extends InfoLabelAndTextProps, PerButtonCircleProps {}

export type AppraisalSeasonInfoBoxProps = {
  data: AppraisalSeasonOpeningInfoDto
}

export const AppraisalSeasonInfoBox: FC<AppraisalSeasonInfoBoxProps> = ({ data }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [chosenMonth, setChosenMonth] = useState<number>(data.nextAppraisalSeasonOpeningMonth)
  const [chosenYear, setChosenYear] = useState<number>(data.nextAppraisalSeasonOpeningYear || data.currentAppraisalSeasonOpeningYear + 1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isModalOpened, setIsModalOpened] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selected] = useHRSettingsStore((state) => [state.selected], shallow)

  const timezoneAndLocale: TimeZoneAndLocale = {
    locale: user.locale,
    timezone: data.defaultCountryTimezone,
  }

  const { now, utcDateStringToMoment } = useDateTime(timezoneAndLocale)

  useEffect(() => {
    const sendData = async () => {
      return setNextAppraisalSeasonOpeningDate(selected.companyId, selected.country, data.nextAppraisalSeasonYear, chosenMonth, chosenYear)
    }
    if (isSubmitting) {
      sendData()
        .then((res) => {
          if (!!res.errors && !!res.errors.length) {
            setError('companySettingsView.appraisalSeasonTab.infoBox.errorMessage')
          } else {
            showNotification({
              type: 'success',
              message: t('companySettingsView.appraisalSeasonTab.infoBox.notifications.success.title'),
              description: t('companySettingsView.appraisalSeasonTab.infoBox.notifications.success.description'),
            })
            setError(null)
            setIsModalOpened(false)
          }
        })
        .catch((error) => {
          setError(error)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }, [t, error, chosenMonth, chosenYear, data.nextAppraisalSeasonYear, isSubmitting, selected.companyId, selected.country])

  const changeChosenDate = (date: string) => {
    const newDate: Moment | undefined = utcDateStringToMoment(date)
    if (!!newDate) {
      setChosenMonth(newDate.month())
      setChosenYear(newDate.year())
    }
  }

  const closeModalWithoutConfirming = () => {
    setIsModalOpened(false)
    setChosenMonth(data.nextAppraisalSeasonOpeningMonth)
    setChosenYear(data.nextAppraisalSeasonOpeningYear || data.currentAppraisalSeasonOpeningYear + 1)
  }

  const capitalizeFirstLetter = (word: string): string => {
    return word.charAt(0).toLocaleUpperCase() + word.slice(1)
  }

  const InfoLabelAndText: FC<InfoLabelAndTextProps> = ({ label, text, labelClassName, textClassName, rowClassName }) => {
    return (
      <PerFlexRow align="middle" className={rowClassName && `per-companySettings--appraisalSeason-header-${rowClassName}`}>
        <div style={{ width: '60%' }}>
          <PerText className={labelClassName && `per-companySettings--appraisalSeason-header-${labelClassName}`}>{label}</PerText>
        </div>
        <div style={{ width: '25%' }}>
          <PerText className={textClassName && `per-companySettings--appraisalSeason-header-${textClassName}`}>{text}</PerText>
        </div>
      </PerFlexRow>
    )
  }

  const InfoLabelAndTextAndButton: FC<InfoLabelAndTextAndButtonProps> = ({ ...props }) => {
    return (
      <>
        <PerFlexRow align="middle" className={props.rowClassName && `per-companySettings--appraisalSeason-header-${props.rowClassName}`}>
          <div style={{ width: '60%' }}>
            <PerText className={props.labelClassName && `per-companySettings--appraisalSeason-header-${props.labelClassName}`}>
              {props.label}
            </PerText>
          </div>
          <div style={{ width: '25%' }}>
            <PerText className={props.textClassName && `per-companySettings--appraisalSeason-header-${props.textClassName}`}>
              {props.text}
            </PerText>
          </div>
          <PerButtonCircle
            icon={props.icon}
            color={props.color}
            variant={props.variant}
            tooltip={props.tooltip}
            onClick={props.onClick}
            disabled={isSubmitting}
            style={{ marginLeft: '0' }}
          />
        </PerFlexRow>
      </>
    )
  }

  return (
    <PerInfoBox>
      <InfoBoxTile
        heading={t('companySettingsView.appraisalSeasonTab.infoBox.heading')}
        contentFlex="growShrink"
        headingClassName="per-companySettings--appraisalSeason-header-heading"
      >
        <PerFlexRow direction="column" width="50%">
          <InfoLabelAndText
            label={t('companySettingsView.appraisalSeasonTab.infoBox.defaultStartingDateLabel')}
            text={capitalizeFirstLetter(
              moment([data.currentAppraisalSeasonOpeningYear, data.defaultAppraisalSeasonOpeningMonth, 1]).format('MMMM'),
            )}
            labelClassName="grayText"
            textClassName="text"
            rowClassName="firstTextRow"
          ></InfoLabelAndText>
          <InfoLabelAndText
            label={t('companySettingsView.appraisalSeasonTab.infoBox.currentStartingDateLabel', { year: data.currentAppraisalSeasonYear })}
            text={
              data.currentAppraisalSeasonYear !== undefined &&
              data.currentAppraisalSeasonYear !== null &&
              data.currentAppraisalSeasonOpeningMonth !== undefined &&
              data.currentAppraisalSeasonOpeningMonth !== null
                ? capitalizeFirstLetter(
                    moment([data.currentAppraisalSeasonOpeningYear, data.currentAppraisalSeasonOpeningMonth, 1]).format('MMMM YYYY'),
                  )
                : undefined
            }
            textClassName="text"
            labelClassName="grayText"
            rowClassName="secondTextRow"
          ></InfoLabelAndText>
          <InfoLabelAndTextAndButton
            label={t('companySettingsView.appraisalSeasonTab.infoBox.nextSeasonStartingDateLabel', { year: data.nextAppraisalSeasonYear })}
            labelClassName="grayText"
            text={capitalizeFirstLetter(moment([chosenYear, chosenMonth, 1]).format('MMMM YYYY'))}
            textClassName="text"
            rowClassName="textRow"
            icon="edit_fill"
            color="primary"
            variant="pastel"
            tooltip={t('tooltip.edit')}
            onClick={() => {
              setIsModalOpened(true)
            }}
          ></InfoLabelAndTextAndButton>
        </PerFlexRow>
      </InfoBoxTile>
      <PerModal
        visible={isModalOpened}
        title={{
          heading: t('companySettingsView.appraisalSeasonTab.infoBox.modal.heading'),
          subheading: t('companySettingsView.appraisalSeasonTab.infoBox.modal.subHeading'),
        }}
        size="small"
        borderRadius="10"
        centered
        onCancel={closeModalWithoutConfirming}
        footer={{
          cancelBtn: {
            onClick: closeModalWithoutConfirming,
            visible: !isSubmitting,
          },
          confirmBtn: {
            text: 'save',
            disabled: isSubmitting || (chosenMonth === data.nextAppraisalSeasonOpeningMonth && chosenYear === data.nextAppraisalSeasonYear),
            isProcessing: { active: isSubmitting },
            eventHandler: {
              onMouseDown: () => {
                setIsSubmitting(true)
              },
            },
          },
        }}
      >
        <PerFlexRow width="fullWidth" align="middle" justify="start">
          <PerDatePicker
            allowClear={false}
            showToday={false}
            disabled={isSubmitting}
            inputDate={capitalizeFirstLetter(moment([chosenYear, chosenMonth, 1]).tz(data.defaultCountryTimezone).toISOString())}
            format={'MM/YYYY'}
            dateFormat="MY"
            timeZoneAndLocale={timezoneAndLocale}
            size="default"
            width={'62%'}
            disabledDate={(current: Moment | null) => {
              const momentNow: Moment = now()
              return (
                !!current &&
                (![data.nextAppraisalSeasonYear - 1, data.nextAppraisalSeasonYear].includes(current.year()) ||
                  (current.year() === data.nextAppraisalSeasonYear && current.month() !== 0) ||
                  (current.year() === data.nextAppraisalSeasonYear - 1 && current.month() === 0) ||
                  current < momentNow ||
                  (momentNow.month() + 1 === data.nextAppraisalSeasonOpeningMonth &&
                    momentNow.year() === data.nextAppraisalSeasonOpeningYear &&
                    momentNow.tz('UTC').date() >= 27))
              )
            }}
            onDateChanged={(date) => {
              !!date && changeChosenDate(date)
            }}
          />
        </PerFlexRow>
        {error && (
          <PerText color="error" fontSize="m" mt="m">
            {error}
          </PerText>
        )}
      </PerModal>
    </PerInfoBox>
  )
}
